import React, { ReactNode, useMemo, useRef, useState } from "react";
import cn from "classnames";
import ColumnResizer from "./ColumnResizer";
import useColumnDrag from "./useColumnDrag";
import useColumnLayout from "./useColumnLayout";
import useInfiniteRows from "../../hooks/useInfiniteRows";
import useIsPhone from "../../hooks/useIsPhone";
import DataCards from "./DataCards";
import { gsap, prefersReducedMotion, SETTLE, useGSAP } from "../../motion/gsap";
import styles from "./styles.module.scss";

export type DataColumn<T> = {
  key: string;
  title: string;
  /** Default width in px; a width the user dragged wins over it. */
  width: number;
  render: (row: T) => ReactNode;
  sorter?: (a: T, b: T) => number;
  align?: "left" | "center";
  /** Pinned to the left edge and kept first: it cannot be dragged. */
  fixed?: boolean;
  /**
   * Where the column goes on a phone's card (DataCards): "title" heads it
   * instead of the pinned column, "header" sits beside the title without a
   * label (a status), "full" takes a line, "footer" closes the card without
   * a label (actions), "hidden" stays off it. By default it shares a grid.
   */
  mobile?: "title" | "header" | "full" | "footer" | "hidden";
};

export type SortState = { key: string; direction: "asc" | "desc" } | null;

type DataTableProps<T> = {
  /** Where the user's column order and widths are kept between visits. */
  id: string;
  columns: DataColumn<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => React.Key;
  defaultSort?: SortState;
  /** Cells of the Totals row by column key; the row stays at the bottom. */
  totals?: Record<string, ReactNode>;
  emptyText?: string;
  /** Changes with the page's filters: the rows start again from the top. */
  resetKey?: string;
  /** On a phone: keys that lead each card's body, in this order. */
  cardOrder?: string[];
};

/**
 * A table drawn like the client's Apple Ads tables: the header and the
 * Totals row stay put while the rows scroll between them, the first column
 * is pinned, and the columns are resized by the handle on a header's right
 * edge and reordered by dragging the header. The rows come in pages as the
 * table is scrolled; the Totals row is the caller's, over all of them.
 */
function DataTable<T>({
  id,
  columns,
  rows,
  rowKey,
  defaultSort = null,
  totals,
  emptyText = "No data",
  resetKey = "",
  cardOrder,
}: DataTableProps<T>) {
  const areaRef = useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();
  const [sort, setSort] = useState<SortState>(defaultSort);
  const layout = useColumnLayout(
    id,
    columns.map((column) => column.key),
  );
  const { dragProps, dragKey, overKey } = useColumnDrag(layout.reorder);

  const byKey = new Map(columns.map((column) => [column.key, column]));
  // Pinned columns stay first whatever order was saved.
  const ordered = layout.order
    .map((key) => byKey.get(key) as DataColumn<T>)
    .sort((a, b) => Number(Boolean(b.fixed)) - Number(Boolean(a.fixed)));
  const widthOf = (column: DataColumn<T>) =>
    layout.widths[column.key] ?? column.width;
  const tableWidth = ordered.reduce((sum, column) => sum + widthOf(column), 0);

  const sorter = sort ? byKey.get(sort.key)?.sorter : undefined;
  const sortedRows = useMemo(() => {
    if (!sort || !sorter) return rows;
    const copy = [...rows];
    copy.sort((a, b) =>
      sort.direction === "asc" ? sorter(a, b) : -sorter(a, b),
    );
    return copy;
  }, [rows, sort, sorter]);
  const { visibleRows, sentinelRef } = useInfiniteRows(
    sortedRows,
    JSON.stringify([resetKey, sort]),
  );

  // The first screen of rows settles in from the top when the data arrives
  // or the filters change; a sort or a refetch reorders them in place.
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const playedFor = useRef<string | null>(null);
  const hasRows = sortedRows.length > 0;
  useGSAP(
    () => {
      const body = bodyRef.current;
      if (!body || !hasRows || playedFor.current === resetKey) return;
      playedFor.current = resetKey;
      if (prefersReducedMotion()) return;
      gsap.from(Array.from(body.rows).slice(0, 20), {
        y: 6,
        autoAlpha: 0,
        duration: 0.35,
        stagger: 0.02,
        clearProps: SETTLE,
      });
    },
    { dependencies: [resetKey, hasRows, isPhone] },
  );

  const changeSort = (key: string) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );

  // The client's sort arrows: both in quaternary, the active one darker.
  const renderSortIcon = (key: string) => {
    const direction = sort?.key === key ? sort.direction : undefined;
    return (
      <svg
        className={styles.sortIcon}
        viewBox="0 0 16 24"
        fill="currentColor"
        aria-hidden
      >
        <path
          d="M8 6L12 11H4L8 6Z"
          className={direction === "asc" ? styles.sortActive : undefined}
        />
        <path
          d="M8 18L4 13H12L8 18Z"
          className={direction === "desc" ? styles.sortActive : undefined}
        />
      </svg>
    );
  };

  if (isPhone)
    return (
      <DataCards
        columns={columns}
        rows={visibleRows}
        rowKey={rowKey}
        emptyText={emptyText}
        totals={totals}
        order={cardOrder}
        sort={sort}
        onSortChange={setSort}
        sortable={columns
          .filter((column) => column.sorter)
          .map(({ key, title }) => ({ key, title }))}
        sentinelRef={sentinelRef}
      />
    );

  const cellClass = (column: DataColumn<T>) =>
    cn({
      [styles.center]: column.align === "center",
      [styles.fixed]: column.fixed,
    });

  return (
    <div ref={areaRef} className={styles.scroller}>
      <table className={styles.table} style={{ minWidth: tableWidth }}>
        <colgroup>
          {ordered.map((column) => (
            <col key={column.key} style={{ width: widthOf(column) }} />
          ))}
          {/* Takes what the columns leave, so the rows reach the card edge. */}
          <col />
        </colgroup>
        <thead>
          <tr>
            {ordered.map((column) => (
              <th
                key={column.key}
                className={cn(styles.headerCell, cellClass(column), {
                  [styles.draggable]: !column.fixed,
                  [styles.dragging]: dragKey === column.key,
                  [styles.dropTarget]: overKey === column.key,
                })}
                {...(column.fixed ? {} : dragProps(column.key))}
              >
                {column.sorter ? (
                  <button
                    type="button"
                    className={styles.headerLabel}
                    onClick={() => changeSort(column.key)}
                  >
                    <span className={styles.headerText}>{column.title}</span>
                    {renderSortIcon(column.key)}
                  </button>
                ) : (
                  <span className={styles.headerText}>{column.title}</span>
                )}
                <ColumnResizer
                  boundsRef={areaRef}
                  label={column.title}
                  onResize={(width) => layout.resize(column.key, width)}
                />
              </th>
            ))}
            <th className={styles.headerCell} aria-hidden />
          </tr>
        </thead>
        <tbody ref={bodyRef}>
          {sortedRows.length === 0 ? (
            <tr>
              <td className={styles.empty} colSpan={ordered.length + 1}>
                {emptyText}
              </td>
            </tr>
          ) : (
            visibleRows.map((row, index) => (
              <tr key={rowKey(row, index)}>
                {ordered.map((column) => {
                  const content = column.render(row);
                  return (
                    <td
                      key={column.key}
                      className={cn(styles.cell, cellClass(column))}
                      // Cut text is still readable on hover.
                      title={
                        typeof content === "string" ||
                        typeof content === "number"
                          ? String(content)
                          : undefined
                      }
                    >
                      {content}
                    </td>
                  );
                })}
                <td className={styles.cell} />
              </tr>
            ))
          )}
        </tbody>
        {totals && sortedRows.length > 0 && (
          <tfoot>
            <tr>
              {ordered.map((column) => (
                <td
                  key={column.key}
                  className={cn(styles.totalsCell, cellClass(column))}
                >
                  {totals[column.key] ?? null}
                </td>
              ))}
              <td className={styles.totalsCell} />
            </tr>
          </tfoot>
        )}
      </table>
      <div ref={sentinelRef} aria-hidden />
    </div>
  );
}

export default DataTable;
